/* eslint-disable no-redeclare */

/**
 * Shared API Client using native fetch
 *
 * USAGE PATTERNS:
 * ✅ CORRECT: api.get("/leads") → becomes /api/leads
 * ✅ CORRECT: api.post("/leads", data) → becomes /api/leads
 * ✅ CORRECT: api.put("/leads/123", data) → becomes /api/leads/123
 *
 * ❌ WRONG: api.get("/api/leads") → becomes /api/api/leads (double prefix)
 * ❌ WRONG: api.get("http://localhost:3001/leads") → hardcoded URL
 *
 * All methods expect relative URLs without /api prefix.
 * The client automatically adds the /api prefix and handles authentication.
 */

import { logDebug } from "./utils/logger.js";
import { formatDisplayError } from "./utils/displayError.js";
// jwt-decode v3 = default export; v4 = named { jwtDecode }. Support both
// (worktree may resolve root hoisted v4 via symlink).
import * as jwtDecodeModule from "jwt-decode";
const jwtDecode = jwtDecodeModule.default || jwtDecodeModule.jwtDecode;

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";
let API_DEBUG = false;
try {
  API_DEBUG =
    (typeof import.meta !== "undefined" &&
      import.meta.env?.VITE_API_DEBUG === "true") ||
    (typeof window !== "undefined" &&
      window.localStorage?.getItem("API_DEBUG") === "1");
} catch (_) {
  API_DEBUG = false;
}

const debugLog = (...args) => {
  if (API_DEBUG) {
    // Use logger for debug logging
    logDebug(...args);
  }
};
const DEFAULT_TIMEOUT = 30000; // Increased timeout to 30 seconds

/** Refresh access token this many ms before JWT expiry (proactive / sliding session). */
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Handle INVALID_TOKEN by triggering logout
 * This function will be called by the AuthContext
 */
let handleInvalidToken = null;

export const setInvalidTokenHandler = (handler) => {
  handleInvalidToken = handler;
};

/** Optional: AuthContext registers an async refresh callback (returns new token or null). */
let tokenRefreshHandler = null;
let tokenRefreshInFlight = null;

export const setTokenRefreshHandler = (handler) => {
  tokenRefreshHandler = handler;
};

/**
 * Get auth token from localStorage
 * @returns {string|null} Auth token or null
 */
const getAuthToken = () => {
  try {
    return localStorage.getItem("accessToken");
  } catch (error) {
    return null;
  }
};

const ensureAccessTokenFresh = async () => {
  if (!tokenRefreshHandler) return;
  const token = getAuthToken();
  if (!token) return;
  let expMs;
  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return;
    expMs = decoded.exp * 1000;
  } catch {
    return;
  }
  if (expMs - Date.now() > REFRESH_BEFORE_EXPIRY_MS) return;
  if (!tokenRefreshInFlight) {
    tokenRefreshInFlight = Promise.resolve(tokenRefreshHandler()).finally(
      () => {
        tokenRefreshInFlight = null;
      },
    );
  }
  await tokenRefreshInFlight;
};

/**
 * Handle common HTTP errors
 * @param {Response} response - Fetch response object
 * @returns {Response} Response object
 * @throws {Error} If response is not ok
 */
const handleResponse = (response) => {
  if (!response.ok) {
    throw response; // throw the raw response for further handling
  }
  return response;
};

/**
 * Create request headers with auth token
 * @param {Object} customHeaders - Additional headers
 * @param {boolean} includeAuth - Whether to include Authorization header
 * @returns {Object} Headers object
 */
const createHeaders = (customHeaders = {}, includeAuth = true) => {
  const headers = {
    "Content-Type": "application/json",
    ...customHeaders,
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
};

/**
 * Create fetch request with timeout
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>} Fetch response
 */
const fetchWithTimeout = async (
  url,
  options = {},
  timeout = DEFAULT_TIMEOUT,
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      credentials: "include", // Include cookies for refresh token
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error("Request timeout");
    }
    throw error;
  }
};

/**
 * Read error response body once (avoids clone/bodyUsed issues).
 * @param {Response} response
 * @returns {Promise<Object>}
 */
const readErrorResponseBody = async (response) => {
  const text = await response.text();
  if (!text) {
    return { error: `HTTP ${response.status}` };
  }
  try {
    return JSON.parse(text);
  } catch {
    const snippet = text.replace(/\s+/g, " ").trim().slice(0, 160);
    return {
      error: `Unexpected response (HTTP ${response.status}). Check API URL and backend.`,
      raw: snippet,
    };
  }
};

/**
 * Parse JSON response safely
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed JSON data
 */
const parseJson = async (response) => {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    }
    return { success: true, data: await response.text() };
  } catch (error) {
    return { success: true, data: null };
  }
};

/**
 * Make HTTP request
 * @param {string} url - Request URL (relative path without /api prefix)
 * @param {Object} options - Request options
 *  - skipAuth: boolean → do not send Authorization header
 *  - skipAuthRedirect: boolean → do not auto-redirect on 401
 * @returns {Promise<Object>} Response data
 */
const apiRequest = async (url, options = {}) => {
  try {
    // Extract internal flags
    const {
      skipAuth = false,
      skipAuthRedirect = false,
      ...restOptions
    } = options;

    // Ensure URL starts with / and doesn't have double /api prefix
    let cleanUrl = url.startsWith("/") ? url : `/${url}`;

    // Remove any existing /api prefix to prevent double prefix
    if (cleanUrl.startsWith("/api/")) {
      cleanUrl = cleanUrl.substring(4); // Remove "/api" prefix
    }

    const isRefreshRequest = cleanUrl === "/auth/refresh";
    if (!skipAuth && !isRefreshRequest) {
      await ensureAccessTokenFresh();
    }

    // Construct full URL with API base
    const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${cleanUrl}`;

    debugLog("🔍 [API] Request details:", {
      originalUrl: url,
      cleanUrl,
      fullUrl,
      apiBaseUrl: API_BASE_URL,
      skipAuth,
      skipAuthRedirect,
    });

    // Detect FormData to avoid setting JSON content-type
    const isFormData =
      typeof FormData !== "undefined" && restOptions?.body instanceof FormData;

    const headers = createHeaders(restOptions.headers, !skipAuth);
    if (isFormData && headers["Content-Type"]) {
      delete headers["Content-Type"]; // Let the browser set multipart boundary
    }

    const response = await fetchWithTimeout(fullUrl, {
      ...restOptions,
      headers,
    });

    const handledResponse = handleResponse(response);
    const data = await parseJson(handledResponse);

    // Handle INVALID_CREDENTIALS (login error)
    if (data && data.code === "INVALID_CREDENTIALS") {
      // Just return the error object, let the login form handle it
      return data;
    }

    return data;
  } catch (error) {
    // If error is a Response object, try to parse its JSON for error details
    if (error instanceof Response) {
      try {
        const status = error.status;
        const errorData = await readErrorResponseBody(error);

        if (errorData && errorData.code === "INVALID_CREDENTIALS") {
          return errorData;
        }

        // Handle TOKEN_EXPIRED and MISSING_TOKEN (logout)
        if (
          status === 401 &&
          errorData &&
          (errorData.code === "TOKEN_EXPIRED" ||
            errorData.code === "INVALID_TOKEN" ||
            errorData.code === "MISSING_TOKEN")
        ) {
          debugLog("🔐 [API] Authentication error detected:", errorData.code);
          // If caller opts out of redirect behavior, just throw
          if (options.skipAuthRedirect) {
            throw new Error(`${status} ${errorData.message || "Unauthorized"}`);
          }
          if (handleInvalidToken) {
            handleInvalidToken();
          } else {
            // Fallback: clear tokens and redirect
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            window.location.href = "/login?reason=expired";
          }
          throw new Error("Session expired. Please log in again.");
        }

        // Extract error message - check multiple possible fields
        const errorMessage = formatDisplayError(
          errorData,
          "API request failed"
        );

        // Create error with preserved structure for service layer access
        const apiError = new Error(errorMessage);
        apiError.status = status;
        apiError.code = errorData?.code;
        // Preserve full error structure so services can access error.response.data.error
        apiError.response = {
          data: {
            ...errorData,
            // Ensure error field exists with the extracted message
            error: errorMessage,
          },
        };
        throw apiError;
      } catch (parseErr) {
        // Check if parseErr.message contains the actual backend error message
        // (This happens when the body was consumed but the error message is in the exception)
        const errorMessage =
          parseErr.message &&
          !parseErr.message.includes("JSON") &&
          !parseErr.message.includes("Unexpected") &&
          parseErr.message.length > 20 // Likely a real error message, not a parse error
            ? parseErr.message
            : `${error.status || 0} API request failed`;

        const status = error.status || 0;
        const apiError = new Error(errorMessage);
        apiError.status = status;
        apiError.response = { data: { error: errorMessage } };
        throw apiError;
      }
    }
    throw error;
  }
};

/**
 * API client methods
 * All methods expect relative URLs without /api prefix
 * Example: api.get("/leads") → /api/leads
 */
const api = {
  /**
   * GET request
   * @param {string} url - Request URL (relative path, e.g., "/leads")
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  get: (url, options = {}) => {
    let finalUrl = url;
    if (options.params && typeof options.params === "object") {
      const searchParams = new URLSearchParams(options.params).toString();
      if (searchParams) {
        finalUrl += (finalUrl.includes("?") ? "&" : "?") + searchParams;
      }
    }
    // Remove params from options so it doesn't get passed to fetch
    // eslint-disable-next-line no-unused-vars
    const { params, ...rest } = options;
    return apiRequest(finalUrl, { ...rest, method: "GET" });
  },

  /**
   * POST request
   * @param {string} url - Request URL (relative path, e.g., "/leads")
   * @param {Object} data - Request body
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  post: (url, data = {}, options = {}) =>
    apiRequest(url, {
      ...options,
      method: "POST",
      body:
        typeof FormData !== "undefined" && data instanceof FormData
          ? data
          : JSON.stringify(data),
    }),

  /**
   * PUT request
   * @param {string} url - Request URL (relative path, e.g., "/leads/123")
   * @param {Object} data - Request body
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  put: (url, data = {}, options = {}) =>
    apiRequest(url, {
      ...options,
      method: "PUT",
      body:
        typeof FormData !== "undefined" && data instanceof FormData
          ? data
          : JSON.stringify(data),
    }),

  /**
   * PATCH request
   * @param {string} url - Request URL (relative path, e.g., "/leads/123")
   * @param {Object} data - Request body
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  patch: (url, data = {}, options = {}) =>
    apiRequest(url, {
      ...options,
      method: "PATCH",
      body:
        typeof FormData !== "undefined" && data instanceof FormData
          ? data
          : JSON.stringify(data),
    }),

  /**
   * DELETE request
   * @param {string} url - Request URL (relative path, e.g., "/leads/123")
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  delete: (url, options = {}) =>
    apiRequest(url, { ...options, method: "DELETE" }),

  /**
   * Binary request for file downloads
   * @param {string} url - Request URL (relative path, e.g., "/leads/download/123")
   * @param {Object} params - Query parameters
   * @param {string} acceptHeader - Accept header for the request
   * @returns {Promise<Response>} Fetch response object for binary data
   */
  binary: async (url, params = {}, acceptHeader = "application/zip") => {
    // Ensure URL starts with / and doesn't have double /api prefix
    let cleanUrl = url.startsWith("/") ? url : `/${url}`;
    if (cleanUrl.startsWith("/api/")) {
      cleanUrl = cleanUrl.substring(4);
    }

    if (cleanUrl !== "/auth/refresh") {
      await ensureAccessTokenFresh();
    }

    // Build URL with parameters
    let finalUrl = cleanUrl;
    if (Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params).toString();
      if (searchParams) {
        finalUrl += (finalUrl.includes("?") ? "&" : "?") + searchParams;
      }
    }

    // Construct full URL
    const fullUrl = url.startsWith("http")
      ? finalUrl
      : `${API_BASE_URL}${finalUrl}`;

    debugLog("🔍 [API] Binary request details:", {
      originalUrl: url,
      cleanUrl,
      finalUrl,
      fullUrl,
      apiBaseUrl: API_BASE_URL,
    });

    // Create headers with auth token
    const headers = createHeaders({
      Accept: acceptHeader,
    });

    debugLog("🔍 [API] Binary request headers:", headers);

    // Make the request with timeout
    const response = await fetchWithTimeout(fullUrl, {
      method: "GET",
      headers,
    });

    debugLog("🔍 [API] Binary response received:", {
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get("content-type"),
      contentLength: response.headers.get("content-length"),
    });

    // Handle authentication errors
    if (!response.ok) {
      if (response.status === 401) {
        try {
          const errorData = await response.json();
          if (
            errorData &&
            (errorData.code === "TOKEN_EXPIRED" ||
              errorData.code === "INVALID_TOKEN" ||
              errorData.code === "MISSING_TOKEN")
          ) {
            debugLog(
              "🔐 [API] Authentication error in binary request:",
              errorData.code,
            );
            if (handleInvalidToken) {
              handleInvalidToken();
            } else {
              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
              window.location.href = "/login?reason=expired";
            }
            throw new Error("Session expired. Please log in again.");
          }
          throw new Error(
            `${response.status} ${errorData.error || "Download failed"}`,
          );
        } catch (parseErr) {
          throw new Error(`${response.status} Download failed`);
        }
      }
      throw new Error(
        `Download failed: ${response.status} ${response.statusText}`,
      );
    }

    return response;
  },
};

export default api;
